import { createSkyCathedralCourt } from './223-sky-cathedral-court.mjs';
import { createSkyAmbientWorld } from './217-sky-ambient-world.mjs';
import { createSkyImportedWorld, skyImportedWorldStyle } from './216-sky-imported-worlds.mjs';
import { createSkyThemedGroundMaterial, setSkyThemedSurface } from './218-sky-world-surface.mjs';
import { createSkyGroundMaterial } from './174-sky-ground-material.mjs';
import { skyLeviathanJourneyView } from '../../../packages/domain/src/105-sky-leviathan-journey.mjs';
import { SKY_DECORATIVE_QUALITY, applySkyDecorativeField } from './172-sky-adaptive-quality.mjs';
import {
  SKY_TERRAIN,
  skyTerrainSample,
  skyTerrainHeight,
  skyTerrainMeshHeight,
} from '../../../packages/domain/src/87-sky-terrain.mjs';
import {
  SKY_WORLD,
  SKY_WORLD_SCENERY,
  skySceneryScale,
  skyObstacleDimensions,
  skyGroundTravelSpeed,
} from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { createSkyIndustrialWorld } from './165-sky-industrial-world.mjs';
import { createSkyAtmosphere } from './188-sky-atmosphere.mjs';
/** Layered landscape matte paintings plus real instanced Blender architecture.
 * Authored props stay outside the flight corridor; active time drives travel. */
export async function createSkyWorld(T, scene, models, valid, reduced = () => false) {
  const { mergeGeometries } = await import('three/addons/utils/BufferGeometryUtils.js');
  const resources = new Set(),
    textures = [],
    fields = [],
    nodes = new Set(),
    requests = new Set(),
    obstacles = new Set();
  const previousBackground = scene.background;
  let cathedralCourt;
  const industrial = createSkyIndustrialWorld(T);
  const imported = createSkyImportedWorld(T, valid);
  const atmosphere = createSkyAtmosphere(T, scene);
  const add = (node) => {
    nodes.add(node);
    scene.add(node);
    return node;
  };
  const own = (x) => {
    resources.add(x);
    return x;
  };
  function dispose() {
    industrial.dispose();
    cathedralCourt?.dispose();
    imported.dispose();
    atmosphere.dispose();
    for (const obstacle of [...obstacles]) releaseObstacle(obstacle);
    for (const request of requests) request.abort();
    requests.clear();
    for (const node of nodes) scene.remove(node);
    nodes.clear();
    if (textures.includes(scene.background)) scene.background = previousBackground;
    for (const value of resources) value.dispose();
    resources.clear();
  }
  function releaseObstacle(group) {
    if (!obstacles.delete(group)) return;
    for (const material of group.userData.obstacleMaterials) material.dispose();
    group.removeFromParent();
  }
  function obstacle(item) {
    const group =
      models.models[item.obstacleForm === 'ruin' ? 'obelisk' : 'rock'].scene.clone(true);
    const box = new T.Box3().setFromObject(group);
    const center = box.getCenter(new T.Vector3());
    const dimensions = skyObstacleDimensions(item.obstacleForm, item.radius);
    if (!dimensions) throw new Error('Obstacle requires a positive physical radius.');
    const holder = new T.Group();
    const materials = new Map();
    group.traverse((node) => {
      if (!node.isMesh) return;
      const clone = (material) => {
        if (!materials.has(material)) {
          const owned = material.clone();
          owned.userData.baseColor = owned.color.clone();
          materials.set(material, owned);
        }
        return materials.get(material);
      };
      node.material = Array.isArray(node.material)
        ? node.material.map(clone)
        : clone(node.material);
    });
    group.position.sub(center);
    const normalized = new T.Group();
    normalized.add(group);
    normalized.scale.setScalar(dimensions.modelScale);
    normalized.position.y = (item.groundY ?? SKY_WORLD.groundY) - item.y + dimensions.height / 2;
    holder.add(normalized);
    if (Number.isFinite(item.terrainTravel))
      holder.rotation.x = -Math.atan(skyTerrainSample(item.x, item.z, item.terrainTravel).dz);
    holder.userData.obstacleMaterials = [...materials.values()];
    obstacles.add(holder);
    return holder;
  }
  function animateObstacle(group, item) {
    if (Number.isFinite(item.terrainTravel))
      group.rotation.x = -Math.atan(skyTerrainSample(item.x, item.z, item.terrainTravel).dz);
    const dimensions = skyObstacleDimensions(item.obstacleForm, item.radius);
    if (dimensions)
      group.children[0].position.y =
        (item.groundY ?? SKY_WORLD.groundY) - item.y + dimensions.height / 2;
    const damage = 1 - Math.max(0, item.hp) / item.maxHp;
    for (const material of group.userData.obstacleMaterials) {
      material.color.copy(material.userData.baseColor).multiplyScalar(1 - damage * 0.55);
      material.emissive?.setRGB(damage * 0.25, damage * 0.045, 0);
    }
  }
  try {
    async function loadTexture(name, i) {
      const controller = new AbortController();
      requests.add(controller);
      const timeout = setTimeout(() => controller.abort(), 20000);
      let objectUrl;
      try {
        const response = await fetch(`/assets/sky-rider/${name}.webp`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Landscape unavailable');
        objectUrl = URL.createObjectURL(await response.blob());
        const texture = await new Promise((resolve, reject) => {
          const cancel = () => reject(new Error('Landscape request cancelled'));
          controller.signal.addEventListener('abort', cancel, { once: true });
          if (controller.signal.aborted) {
            cancel();
            return;
          }
          own(
            new T.TextureLoader().load(
              objectUrl,
              (loaded) => {
                controller.signal.removeEventListener('abort', cancel);
                if (controller.signal.aborted) {
                  loaded.dispose();
                  return;
                }
                resolve(loaded);
              },
              undefined,
              (error) => {
                controller.signal.removeEventListener('abort', cancel);
                reject(error);
              },
            ),
          );
        });
        texture.colorSpace = T.SRGBColorSpace;
        textures[i] = texture;
      } finally {
        clearTimeout(timeout);
        requests.delete(controller);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }
    }
    // Only loading owns this poll. Navigation/revocation cancels pending network
    // and decode waits; no timer survives successful construction or failure.
    const cancellation = setInterval(() => {
      if (!valid()) for (const request of requests) request.abort();
    }, 100);
    let result;
    try {
      result = await Promise.allSettled(
        ['01-sanctuary', '02-forest', '03-eclipse', '04-stone'].map(loadTexture),
      );
    } finally {
      clearInterval(cancellation);
    }
    if (!valid() || result.some((r) => r.status === 'rejected')) {
      dispose();
      if (!valid()) return null;
      throw new Error('Landscape unavailable');
    }
    const stoneTexture = textures[3];
    stoneTexture.colorSpace = T.SRGBColorSpace;
    stoneTexture.wrapS = stoneTexture.wrapT = T.RepeatWrapping;
    stoneTexture.repeat.set(2, 26);
    stoneTexture.anisotropy = 4;
    if (!valid()) {
      dispose();
      return null;
    }
    // Reuse the authored image as subtle approximate height detail, not a normal map.
    // Data sampling uses its own linear texture view; the albedo remains sRGB.
    const stoneBump = own(stoneTexture.clone());
    stoneBump.colorSpace = T.NoColorSpace;
    const architectureStone = own(stoneTexture.clone());
    architectureStone.repeat.set(1, 1);
    architectureStone.offset.set(0, 0);
    const architectureBump = own(architectureStone.clone());
    architectureBump.colorSpace = T.NoColorSpace;
    function ensureArchitectureUV(geometry) {
      // The original mesh has no atlas. Preserve existing UVs when available;
      // otherwise project by dominant face normal in model metres, so vertical
      // supports and roof facets receive real detail rather than a flat texel.
      if (!geometry.getAttribute('uv')) {
        const positions = geometry.getAttribute('position');
        const normals = geometry.getAttribute('normal');
        const uv = new Float32Array(positions.count * 2);
        for (let i = 0; i < positions.count; i++) {
          const nx = Math.abs(normals.getX(i)),
            ny = Math.abs(normals.getY(i)),
            nz = Math.abs(normals.getZ(i));
          uv[i * 2] = (nx > ny && nx > nz ? positions.getZ(i) : positions.getX(i)) / 1.8;
          uv[i * 2 + 1] = (ny > nx && ny > nz ? positions.getZ(i) : positions.getY(i)) / 1.8;
        }
        geometry.setAttribute('uv', new T.BufferAttribute(uv, 2));
      }
    }
    function weatherArchitecture(geometry, material) {
      if (!['volcanic stone', 'weathered ceramic armour'].includes(material.name)) return;
      ensureArchitectureUV(geometry);
      material.map = architectureStone;
      material.bumpMap = architectureBump;
      material.bumpScale = 0.09;
      material.color.set('#b6b8b0');
      material.roughness = 0.94;
      material.metalness = 0.05;
      material.envMapIntensity = 0.35;
    }
    cathedralCourt = createSkyCathedralCourt(T, architectureStone);
    add(cathedralCourt.group);
    const groundMaterial = own(createSkyGroundMaterial(T, architectureStone));
    const plane = new T.Mesh(
      own(
        new T.PlaneGeometry(
          SKY_TERRAIN.groundWidth,
          SKY_TERRAIN.groundLength,
          SKY_TERRAIN.groundColumns,
          SKY_TERRAIN.groundRows,
        ),
      ),
      groundMaterial,
    );
    plane.rotation.x = -Math.PI / 2;
    plane.position.set(0, SKY_WORLD.terrainY, SKY_TERRAIN.groundCenterZ);
    add(plane);
    // A thin ancient causeway gives walking/running a stable physical surface.
    const roadMaterial = own(
      new T.MeshStandardMaterial({
        color: '#a4a9a8',
        map: stoneTexture,
        bumpMap: stoneBump,
        bumpScale: 0.06,
        roughness: 0.86,
        metalness: 0.12,
      }),
    );
    const road = new T.Mesh(
      own(
        new T.PlaneGeometry(
          SKY_WORLD.corridorWidth,
          210,
          SKY_TERRAIN.roadColumns,
          SKY_TERRAIN.roadRows,
        ),
      ),
      roadMaterial,
    );
    road.rotation.x = -Math.PI / 2;
    road.position.set(0, SKY_WORLD.groundY, -80);
    road.receiveShadow = true;
    road.name = 'sky-running-surface';
    add(road);
    plane.name = 'sky-sculpted-terrain';
    for (const surface of [plane, road]) {
      surface.frustumCulled = false;
      surface.geometry.attributes.position.setUsage(T.DynamicDrawUsage);
      surface.geometry.attributes.normal.setUsage(T.DynamicDrawUsage);
    }
    let lastTerrainTravel = null;
    const terrainSample = { height: 0, dx: 0, dz: 0, normal: [0, 1, 0] };
    function displaceTerrain(travel) {
      if (travel === lastTerrainTravel) return;
      lastTerrainTravel = travel;
      for (const surface of [plane, road]) {
        const positions = surface.geometry.attributes.position;
        const normals = surface.geometry.attributes.normal;
        for (let i = 0; i < positions.count; i++) {
          const sample = skyTerrainSample(
            positions.getX(i),
            surface.position.z - positions.getY(i),
            travel,
            terrainSample,
          );
          positions.setZ(i, sample.height);
          normals.setXYZ(i, sample.normal[0], -sample.normal[2], sample.normal[1]);
        }
        positions.needsUpdate = normals.needsUpdate = true;
      }
    }

    const lineMaterial = own(
      new T.MeshStandardMaterial({
        color: '#baad88',
        emissive: '#a27c39',
        emissiveIntensity: 0.2,
        roughness: 0.65,
      }),
    );
    const lines = new T.InstancedMesh(own(new T.BoxGeometry(0.055, 0.016, 2.7)), lineMaterial, 120);
    lines.name = 'sky-road-markers';
    add(lines);
    own(lines);
    const matrix = new T.Object3D();
    function field(key, descriptor) {
      const { count, template: templateKey } = descriptor;
      let sideOffset = descriptor.sideOffset;
      const template = models.models[templateKey].scene;
      template.updateMatrixWorld(true);
      const byMaterial = new Map();
      template.traverse((node) => {
        if (!node.isMesh || Array.isArray(node.material)) return;
        const geometry = own(node.geometry.clone());
        geometry.applyMatrix4(node.matrixWorld);
        const list = byMaterial.get(node.material) ?? [];
        list.push(geometry);
        byMaterial.set(node.material, list);
      });
      const meshes = [];
      let lateralExtent = 0;
      for (const [material, geometries] of byMaterial) {
        // Blender assemblies can mix primitives with UVs and custom lofts
        // without them. Complete only missing coordinates before batching;
        // mergeGeometries otherwise rejects the whole material region.
        if (geometries.some((geometry) => geometry.hasAttribute('uv')))
          for (const geometry of geometries) ensureArchitectureUV(geometry);
        const merged = mergeGeometries(geometries, false);
        for (const geometry of geometries) {
          geometry.dispose();
          resources.delete(geometry);
        }
        if (!merged) throw new Error('Landscape geometry could not be batched.');
        merged.computeBoundingBox();
        const box = merged.boundingBox;
        lateralExtent = Math.max(
          lateralExtent,
          Math.hypot(
            Math.max(Math.abs(box.min.x), Math.abs(box.max.x)),
            Math.max(Math.abs(box.min.z), Math.abs(box.max.z)),
          ),
        );
        const instanceMaterial = own(material.clone());
        if (templateKey === 'arch') weatherArchitecture(merged, instanceMaterial);
        instanceMaterial.userData.originalColor = instanceMaterial.color.clone();
        instanceMaterial.userData.originalRoughness = instanceMaterial.roughness;
        const mesh = new T.InstancedMesh(own(merged), instanceMaterial, count);
        own(mesh);
        mesh.name = `sky-scenery-${key}`;
        mesh.frustumCulled = false;
        add(mesh);
        meshes.push(mesh);
      }
      // Reserve the full rotated, largest-scale silhouette beyond the flight lane.
      sideOffset = Math.max(
        sideOffset,
        SKY_WORLD.sceneryClearanceX +
          lateralExtent * Math.max(...skySceneryScale(key, descriptor.variation.length - 1)),
      );
      fields.push({ key, count, sideOffset, groundOffset: descriptor.groundOffset, meshes });
    }
    for (const [key, descriptor] of Object.entries(SKY_WORLD_SCENERY))
      if (descriptor.count > 0) field(key, descriptor);
    const drawAmbient = createSkyAmbientWorld(T, models, own, add, reduced, mergeGeometries);
    add(industrial.group);
    add(imported.group);
    const worldTextures = new Map();
    let themedGround = null;
    let industrialReady = null;
    function prepare(stage, options) {
      const style = skyImportedWorldStyle(stage);
      if (style)
        return (async () => {
          const importedTextures = await imported.prepare(stage, options);
          if (!worldTextures.has(style.background)) {
            // Reserve10/11 for the molten crust and basalt material maps.
            const slot = 12 + worldTextures.size;
            worldTextures.set(style.background, slot);
          }
          const slot = worldTextures.get(style.background);
          await Promise.all([
            textures[slot] ? null : loadTexture(style.background, slot),
            textures[11] ? null : loadTexture('worlds/05-basalt-material', 11),
            style.surface !== 'basalt' || textures[10]
              ? null
              : loadTexture('worlds/05-molten-crust-material', 10),
          ]);
          if (!valid()) throw new Error('Landscape preparation cancelled');
          textures[11].wrapS = textures[11].wrapT = T.RepeatWrapping;
          textures[11].repeat.set(6, 52);
          textures[11].anisotropy = 4;
          themedGround ??= own(createSkyThemedGroundMaterial(T, architectureStone, textures[11]));
          if (textures[10]) {
            textures[10].wrapS = textures[10].wrapT = T.RepeatWrapping;
            textures[10].anisotropy = 8;
            themedGround.uniforms.lavaMap.value = textures[10];
          }
          return [...importedTextures, textures[slot], textures[11], textures[10]].filter(Boolean);
        })();
      const released = imported.prepare(stage, options);
      if (stage?.encounterId !== 'gilded-leviathan') return released;
      industrialReady ??= Promise.all([
        released,
        textures[4] ? null : loadTexture('39-golden-cloud-sea', 4),
        textures[5] ? null : loadTexture('40-cloud-bank', 5),
        models.prepareIndustrialWorld(),
      ])
        .then(() => {
          if (!valid()) throw new Error('Landscape preparation cancelled');
          industrial.setCloudTexture(textures[5]);
          industrial.setTowerModel(models.models.refineryTower.scene);
          if (models.models.refineryTowerLod)
            industrial.setTowerLodModel(models.models.refineryTowerLod.scene);
          return [textures[4], textures[5]];
        })
        .catch((error) => {
          industrialReady = null;
          throw error;
        });
      return industrialReady;
    }
    let roadTime = null;
    let roadDistance = 0;
    let roadSpeed = 0;
    function advanceRoad(state, stage) {
      // Match the authored ankle stance speed, not the much faster distant
      // encounter travel. The .92m Run stance spans38% of a20/30-second cycle.
      const target = skyGroundTravelSpeed(state.locomotion, stage.speed);
      const time = state.time;
      if (roadTime === null || time < roadTime) {
        roadTime = time;
        roadDistance = 0;
        roadSpeed = target;
        return roadDistance;
      }
      const dt = time - roadTime;
      if (dt <= 0) return roadDistance;
      // Integrate the exponential velocity exactly so30Hz and120Hz produce
      // the same surface distance; paused redraws consume no clock interval.
      const response = 8;
      const decay = Math.exp(-response * dt);
      roadDistance += target * dt + ((roadSpeed - target) * (1 - decay)) / response;
      roadSpeed = target + (roadSpeed - target) * decay;
      roadTime = time;
      return roadDistance;
    }
    let lastBiome = '',
      backdrop = null;
    function draw(state, stage, aspect, decoration = SKY_DECORATIVE_QUALITY[0]) {
      const factory = stage.encounterId === 'gilded-leviathan';
      cathedralCourt.draw(state);
      const style = skyImportedWorldStyle(stage);
      plane.visible = style?.terrain !== false && !cathedralCourt.group.visible;
      imported.draw(state, stage, decoration, reduced());
      plane.material = style && themedGround ? themedGround : groundMaterial;
      road.material = style?.surface === 'basalt' && themedGround ? themedGround : roadMaterial;
      if (style && themedGround) setSkyThemedSurface(themedGround, style.surface);
      const arena = factory && Boolean(state.leviathan);
      // Portrait collapse framing pulls the camera back to fit the whole hull.
      // Preserve the same atmospheric depth instead of bleaching the subject.
      const crash = factory && state.leviathan?.encounter?.phase === 'crash';
      const fogScale = crash ? 1 / Math.min(1, Math.max(0.3, aspect)) : 1;
      scene.fog.near = factory
        ? (arena ? 85 : 140) * fogScale
        : style
          ? 95
          : stage.artVariant === 'first-dragon'
            ? 75
            : 55;
      scene.fog.far = factory
        ? (arena ? 230 : 300) * fogScale
        : style
          ? 245
          : stage.artVariant === 'first-dragon'
            ? 220
            : 155;
      industrial.draw(state, factory, decoration, reduced());
      atmosphere.draw(state, stage, decoration, reduced());
      groundMaterial.depthWrite = !arena;
      groundMaterial.uniforms.shorelineZ.value = factory
        ? skyLeviathanJourneyView(state).shorelineZ
        : 10000;
      groundMaterial.uniforms.coastVisibility.value = factory
        ? skyLeviathanJourneyView(state).landVisibility
        : 1;
      groundMaterial.uniforms.industrialHaze.value = factory
        ? 1 - (state.leviathan?.systems?.crashDescent ?? 0) * 0.9
        : 0;
      road.visible =
        !factory && !cathedralCourt.group.visible && (!style || style.surface === 'basalt');
      lines.visible = !factory && !style;
      const ice = stage.biome === 'ice';
      const tunnel = stage.biome === 'tunnel';
      const family = style
        ? worldTextures.get(style.background)
        : factory
          ? 4
          : stage.biome === 'fungi'
            ? 1
            : ['void', 'citadel', 'tunnel'].includes(stage.biome)
              ? 2
              : 0;
      const biome = `${stage.worldId}:${stage.biome}:${factory}`;
      if (lastBiome !== biome && textures[family]) {
        lastBiome = biome;
        backdrop = textures[family];
        scene.background = backdrop;
        roadMaterial.color.set(family === 0 ? '#a4b3b0' : family === 1 ? '#9b9aad' : '#b0a08f');
        groundMaterial.uniforms.base.value.set(
          family === 0 ? '#526744' : family === 1 ? '#544e63' : '#554b40',
        );
        groundMaterial.uniforms.accent.value.set(
          family === 0 ? '#a29972' : family === 1 ? '#a18d92' : '#9a8562',
        );
        groundMaterial.uniforms.warm.value = family === 2 ? 1 : 0;
        scene.fog.color.set(family === 0 ? '#aab9ae' : family === 1 ? '#646f8c' : '#a58273');
        if (stage.artVariant === 'first-dragon') scene.fog.color.set('#8baba9');
        if (factory) {
          groundMaterial.uniforms.base.value.set('#484c42');
          groundMaterial.uniforms.accent.value.set('#8d7652');
          scene.fog.color.set('#c6ad7a');
        }
        if (ice) {
          roadMaterial.color.set('#d2e8ec');
          roadMaterial.roughness = 0.45;
          groundMaterial.uniforms.base.value.set('#9eafb1');
          groundMaterial.uniforms.accent.value.set('#daf4ff');
          scene.fog.color.set('#bbd8e5');
        } else roadMaterial.roughness = 0.86;
        roadMaterial.map = style?.surface === 'basalt' ? textures[11] : stoneTexture;
        if (style) {
          roadMaterial.color.set(style.surface === 'basalt' ? '#c1b5a5' : '#79858b');
          roadMaterial.needsUpdate = true;
          scene.fog.color.set(style.fog);
        }
        for (const field of fields)
          for (const mesh of field.meshes) {
            mesh.material.color.copy(mesh.material.userData.originalColor);
            mesh.material.roughness = mesh.material.userData.originalRoughness;
            if (ice && field.key === 'rock') {
              mesh.material.color.multiply(new T.Color('#bddeee'));
              mesh.material.roughness = 0.42;
            }
          }
      }
      const imageAspect = 1.5;
      backdrop ??= textures[0];
      const crop = factory ? 0.65 : 1;
      backdrop.repeat.set(
        Math.min(1, aspect / imageAspect) * crop,
        Math.min(1, imageAspect / aspect) * crop,
      );
      backdrop.offset.set((1 - backdrop.repeat.x) / 2, (1 - backdrop.repeat.y) / 2);
      const terrainTravel = Number.isFinite(state.terrainTravel) ? state.terrainTravel : 0;
      displaceTerrain(terrainTravel);
      const scroll = Number.isFinite(state.terrainTravel)
        ? terrainTravel
        : state.time * stage.speed;
      const roadScroll = Number.isFinite(state.terrainTravel)
        ? terrainTravel
        : advanceRoad(state, stage);
      groundMaterial.uniforms.time.value = scroll;
      if (themedGround) {
        themedGround.uniforms.time.value = scroll;
        themedGround.uniforms.flowTime.value = reduced() ? 0 : (state.time ?? 0);
      }
      // Plane UV +V maps toward negative world Z; positive offset moves texels forward.
      const textureOffset = ((roadScroll * stoneTexture.repeat.y) / 210) % 1;
      stoneTexture.offset.y = textureOffset;
      stoneBump.offset.y = textureOffset;
      for (let i = 0; i < 120; i++) {
        const lane = i % 4,
          row = Math.floor(i / 4);
        matrix.position.set(
          [-14.3, -13.3, 13.3, 14.3][lane],
          0.025,
          16 - ((row * 7 - (roadScroll % 210) + 210) % 210),
        );
        matrix.position.y += skyTerrainHeight(matrix.position.x, matrix.position.z, terrainTravel);
        matrix.rotation.set(
          -Math.atan(skyTerrainSample(matrix.position.x, matrix.position.z, terrainTravel).dz),
          0,
          0,
        );
        matrix.scale.set(1, 1, 1);
        matrix.updateMatrix();
        lines.setMatrixAt(i, matrix.matrix);
      }
      lines.instanceMatrix.needsUpdate = true;
      for (const field of fields) {
        const show =
          !factory &&
          !style &&
          ((field.key === 'arch' && !ice && !tunnel) ||
            field.key === 'rock' ||
            (field.key === 'tunnel' && tunnel) ||
            (field.key === 'tree' && stage.biome === 'forest') ||
            (field.key === 'mushroom' && stage.biome === 'fungi'));
        const count = applySkyDecorativeField(field, show, decoration);
        if (!show) continue;
        for (let i = 0; i < count; i++) {
          const side = i % 2 ? -1 : 1;
          const row = Math.floor(i / 2);
          const layer = row % 3;
          // Stagger paired clusters across the whole loop instead of repeatedly
          // wrapping a31m lattice. Recycling happens behind the camera and fog.
          const spacing = 300 / (field.count / 2);
          const phase = row * spacing + (i % 2) * spacing * 0.37;
          const z = 48 - ((phase - (scroll % 300) + 300) % 300);
          const emergence = Math.min(1, Math.max(0, (z + 252) / 32));
          matrix.position.set(
            field.key === 'tunnel'
              ? 0
              : side * (field.sideOffset + layer * 8 + Math.sin(row * 2.4) * 2 + 2),
            SKY_WORLD.groundY + field.groundOffset,
            z,
          );
          matrix.position.y +=
            SKY_WORLD.terrainY +
            skyTerrainMeshHeight(matrix.position.x, z, terrainTravel, terrainSample);
          matrix.rotation.set(0, field.key === 'tunnel' ? 0 : i * 1.7, 0);
          // The physical descriptor owns neutral dimensions, variation and any
          // intentional portal/geology axis stretch. Emergence only occurs in fog.
          matrix.scale.fromArray(skySceneryScale(field.key, row * 7 + i)).multiplyScalar(emergence);
          matrix.updateMatrix();
          for (const mesh of field.meshes) mesh.setMatrixAt(i, matrix.matrix);
        }
        for (const mesh of field.meshes) mesh.instanceMatrix.needsUpdate = true;
      }
      drawAmbient(
        state,
        factory || style ? { ...stage, biome: 'void' } : stage,
        style ? { ...decoration, transports: 0 } : decoration,
      );
    }
    return {
      draw,
      cull(camera, viewportHeight) {
        atmosphere.cull(camera);
        imported.cull(camera);
        return industrial.cull(camera, viewportHeight);
      },
      prepare,
      dispose,
      obstacle,
      animateObstacle,
      releaseObstacle,
      warmup(stage) {
        return stage?.encounterId === 'gilded-leviathan'
          ? industrial.group.children.map((mesh) => mesh.clone())
          : [];
      },
    };
  } catch (error) {
    dispose();
    throw error;
  }
}
